import {range} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  areTimesEqual,
  TimeFormatsEnum,
  withTimezone,
} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {FocusModesEnum} from '../../../../libs/shared/core/src/helpers/types/scrollerTypes';
import {VariableHeightScroller} from '../scroll/variableHeightScroller';
import {TimePickerItem} from './timePickerItem';

/*
 * Types.
 */

interface TimePickerProps {
  startDay?: DateTime;
  selectedTime?: DateTime;
  minTime?: DateTime;
  maxTime?: DateTime;
  onTimeSelect?: (time: DateTime) => void;
  timeFormat: TimeFormatsEnum;
  timezone?: string;
}

/*
 * Style.
 */

const StyledTimeScroller = styled(VariableHeightScroller)`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 12px;

  background-color: ${(p) => p.theme.foreground};
`;

/*
 * Component.
 */

export const TimePicker: FC<TimePickerProps> = (props) => {
  const focusedDay = withTimezone(props.startDay || props.selectedTime || DateTime.local(), props.timezone);
  const focusedHour = withTimezone(props.selectedTime || props.startDay || DateTime.local(), props.timezone);

  return (
    <StyledTimeScroller focusKey={focusedHour.hour} focusMode={FocusModesEnum.MIDDLE}>
      {renderItems(props, focusedDay)}
    </StyledTimeScroller>
  );
};

function renderItems(props: TimePickerProps, focusedDay: DateTime) {
  // Render each hour in the day.
  const {selectedTime, minTime, maxTime, onTimeSelect, timeFormat, timezone} = props;
  return range(24).map((hour) => {
    // Check if this hour is selected.
    const hourTime = focusedDay.startOf('hour').set({hour});
    const isSelected = areTimesEqual(hourTime, selectedTime);

    // Also, check if it's selectable (within bounds).
    const isAfterMinTime = !minTime || minTime < hourTime;
    const isBeforeMaxTime = !maxTime || maxTime > hourTime;
    const isSelectable = isAfterMinTime && isBeforeMaxTime;

    return (
      <TimePickerItem
        key={hour}
        hour={hourTime}
        isSelected={isSelected}
        isSelectable={isSelectable}
        onSelect={() => onTimeSelect && onTimeSelect(hourTime)}
        timeFormat={timeFormat}
        timezone={timezone}
      />
    );
  });
}
